'use client';
import React, { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

interface SuccessConfirmationProps {
  show: boolean;
  title: string;
  subtitle?: string;
  onDone?: () => void;
  autoDismissMs?: number;
  accentColor?: string;
}

export default function SuccessConfirmation({
  show,
  title,
  subtitle,
  onDone,
  autoDismissMs = 2800,
  accentColor = '#22c55e',
}: SuccessConfirmationProps) {
  const [visible, setVisible] = useState(false);
  const [animPhase, setAnimPhase] = useState<'in' | 'hold' | 'out'>('in');

  useEffect(() => {
    if (!show) return;
    setVisible(true);
    setAnimPhase('in');

    const t1 = setTimeout(() => setAnimPhase('hold'), 400);
    const t2 = setTimeout(() => setAnimPhase('out'), autoDismissMs - 300);
    const t3 = setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, autoDismissMs);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [show]);

  if (!visible) return null;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '32px 48px',
    backgroundColor: 'var(--bg-card)',
    border: `1px solid var(--border)`,
    borderRadius: '14px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    opacity: animPhase === 'out' ? 0 : animPhase === 'in' ? 0 : 1,
    scale: animPhase === 'in' ? '0.85' : animPhase === 'out' ? '0.95' : '1',
    transition: 'opacity 300ms ease, scale 300ms cubic-bezier(0.34,1.56,0.64,1)',
    textAlign: 'center',
    minWidth: '260px',
  };

  const checkStyle: React.CSSProperties = {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: `${accentColor}12`,
    border: `1.5px solid ${accentColor}30`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: animPhase !== 'in' ? 'checkPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' : 'none',
  };

  return (
    <>
      <style>{`
        @keyframes checkPop {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          backgroundColor: 'rgba(0,0,0,0.4)',
          opacity: animPhase === 'out' ? 0 : animPhase === 'in' ? 0 : 1,
          transition: 'opacity 300ms ease',
        }}
      />
      <div style={containerStyle}>
        <div style={checkStyle}>
          <CheckCircle2 size={28} style={{ color: accentColor }} />
        </div>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: subtitle ? '4px' : 0, letterSpacing: '-0.01em' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{subtitle}</div>
          )}
        </div>
        <div style={{ width: '100%', height: '2px', backgroundColor: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              backgroundColor: accentColor,
              borderRadius: '2px',
              width: animPhase === 'out' ? '100%' : '0%',
              transition: animPhase === 'hold' ? `width ${autoDismissMs - 700}ms linear` : 'none',
            }}
          />
        </div>
      </div>
    </>
  );
}
